import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Alert, AlertDescription } from "../components/ui/alert";
import { Separator } from "../components/ui/separator";
import { Skeleton } from "../components/ui/skeleton";
import { CheckCircle2, AlertTriangle, Loader2, Save, Clock, Mail, Send, Inbox, Database } from "lucide-react";
import { toast } from "sonner";
import { AsanaSyncCard } from "../components/AsanaSyncCard";

export default function SettingsPage() {
  const { user } = useAuth();
  const role = user?.role || "viewer";
  const canEdit = ["super_admin", "admin", "manager"].includes(role);
  const [s, setS] = useState(null);
  const [scheduler, setScheduler] = useState(null);
  const [emails, setEmails] = useState([]);
  const [saving, setSaving] = useState(false);
  const [sendingDigest, setSendingDigest] = useState(false);
  const [bulkSyncing, setBulkSyncing] = useState(false);

  const load = async () => {
    const [settings, sch, log] = await Promise.allSettled([
      api.get("/settings"),
      api.get("/scheduler/status"),
      api.get("/email-log", { params: { limit: 20 } }),
    ]);
    if (settings.status === "fulfilled") setS(settings.value.data);
    if (sch.status === "fulfilled") setScheduler(sch.value.data);
    if (log.status === "fulfilled") setEmails(log.value.data.items);
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings", {
        email_alerts_enabled: s.email_alerts_enabled,
        alert_email: s.alert_email,
        auto_daily_check: s.auto_daily_check,
      });
      toast.success("Settings saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const sendTestDigest = async () => {
    setSendingDigest(true);
    try {
      await api.post("/email-log/test-digest");
      toast.success("Digest triggered — check the email log below");
      setTimeout(load, 800);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setSendingDigest(false);
    }
  };

  if (!s) return <div className="p-8"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  return (
    <div className="p-8 max-w-4xl space-y-6 fade-up">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Configuration</div>
        <h1 className="font-heading font-black text-3xl sm:text-4xl tracking-tight mt-1">Settings</h1>
        <p className="text-sm text-muted-foreground mt-1">Control the alert pipeline, EPC register integration, and scheduled jobs.</p>
      </div>

      {canEdit && <AsanaSyncCard isAdmin={["super_admin", "admin"].includes(role)} />}

      {/* EPC API status */}
      <Card className="rounded-none border-border p-6" data-testid="epc-api-status">
        <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">UK Gov EPC Register</div>
        {s.epc_api_configured ? (
          <>
            <Alert className="rounded-none border-emerald-300 bg-emerald-50/40">
              <CheckCircle2 className="h-4 w-4 text-emerald-700" />
              <AlertDescription className="text-emerald-900">
                Live connection to <span className="font-mono">api.get-energy-performance-data.communities.gov.uk</span>.
                Property syncs pull real certificates (band, SAP, floor area, suggested improvements).
              </AlertDescription>
            </Alert>
            <div className="mt-4 flex flex-wrap gap-2 items-center">
              <Button
                size="sm"
                disabled={bulkSyncing}
                onClick={async () => {
                  if (!window.confirm("Run portfolio-wide gov sync? This will iterate ~801 postcodes (~2-3 mins). Runs in background.")) return;
                  setBulkSyncing(true);
                  try {
                    await api.post("/admin/bulk-sync-gov");
                    toast.success("Bulk gov sync started in background", { description: "Check property records over the next few minutes." });
                  } catch (e) {
                    toast.error("Bulk sync failed", { description: e?.response?.data?.detail || e.message });
                  } finally {
                    setBulkSyncing(false);
                  }
                }}
                className="rounded-none bg-emerald-700 hover:bg-emerald-800"
                data-testid="bulk-sync-gov"
              >
                {bulkSyncing ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Database className="h-3.5 w-3.5 mr-1.5" />}
                Run portfolio gov sync
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={async () => {
                  try {
                    const { data } = await api.post("/admin/sync-deltas");
                    toast.success(`Delta sync done — ${data.applied} of ${data.delta_events} events applied`);
                  } catch (e) {
                    toast.error("Delta sync failed", { description: e?.response?.data?.detail || e.message });
                  }
                }}
                className="rounded-none border-slate-300"
                data-testid="sync-deltas"
              >
                Run delta sync (yesterday)
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={async () => {
                  if (!window.confirm("Reconcile properties from the customer source-of-truth spreadsheet (backend/seed_properties_v2.xlsm)?\\n\\nThis upserts by UPRN and marks properties no longer in the file as removed.")) return;
                  try {
                    const { data } = await api.post("/admin/reconcile-xlsx");
                    toast.success("Reconciliation complete", {
                      description: `${data.matched} matched · ${data.inserted} new · ${data.marked_removed} marked removed`,
                    });
                    await load();
                  } catch (e) {
                    toast.error("Reconcile failed", { description: e?.response?.data?.detail || e.message });
                  }
                }}
                className="rounded-none border-slate-300"
                data-testid="reconcile-xlsx"
              >
                Reconcile from spreadsheet
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={async () => {
                  try {
                    const { data } = await api.post("/admin/scan-expiry");
                    toast.success(`Expiry scan complete`, {
                      description: `${data.expired_total} expired · ${data.alerts_created} new alert(s)`,
                    });
                    await load();
                  } catch (e) {
                    toast.error("Expiry scan failed", { description: e?.response?.data?.detail || e.message });
                  }
                }}
                className="rounded-none border-slate-300"
                data-testid="scan-expiry"
              >
                Scan portfolio for expired EPCs
              </Button>
              {s.last_bulk_gov_sync && (
                <span className="text-xs font-mono text-muted-foreground ml-2">
                  Last: {s.last_bulk_gov_sync.postcodes_processed} postcodes · {s.last_bulk_gov_sync.properties_updated} updated
                </span>
              )}
            </div>
          </>
        ) : (
          <Alert className="rounded-none border-amber-300 bg-amber-50/40">
            <AlertTriangle className="h-4 w-4 text-amber-700" />
            <AlertDescription className="text-amber-900">
              Running in <strong>mock mode</strong>. Set <span className="font-mono">EPC_API_TOKEN</span> in the backend{" "}
              <span className="font-mono">.env</span> with a Bearer token from{" "}
              <a href="https://get-energy-performance-data.communities.gov.uk/api-documentation/index.html" target="_blank" rel="noreferrer" className="underline font-medium">
                gov.uk EPB Data API
              </a>{" "}
              to enable live syncs.
            </AlertDescription>
          </Alert>
        )}
      </Card>

      {/* Scheduler status */}
      <Card className="rounded-none border-border p-6" data-testid="scheduler-status">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Background scheduler</div>
            <h2 className="font-heading text-xl font-bold mt-1 flex items-center gap-2">
              <Clock className="h-5 w-5" /> Nightly EPC checks
            </h2>
          </div>
          <span className={`text-xs uppercase tracking-wider font-bold px-2 py-1 ${scheduler?.running ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-600"}`}>
            {scheduler?.running ? "RUNNING" : "STOPPED"}
          </span>
        </div>
        {scheduler?.jobs?.length > 0 ? (
          <div className="space-y-2">
            {scheduler.jobs.map((j) => (
              <div key={j.id} className="border border-border p-3 text-sm" data-testid={`scheduler-job-${j.id}`}>
                <div className="font-medium">{j.name}</div>
                <div className="text-xs text-muted-foreground font-mono mt-1">
                  Next run: {j.next_run ? new Date(j.next_run).toLocaleString("en-GB") : "—"}
                </div>
                <div className="text-xs text-muted-foreground font-mono">{j.trigger}</div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-sm text-muted-foreground">No scheduled jobs.</div>
        )}
      </Card>

      {/* Alert preferences */}
      <Card className="rounded-none border-border p-6 space-y-6">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">Alert preferences</div>
          <h2 className="font-heading font-bold text-xl">How you get notified</h2>
        </div>

        <div className="flex items-start justify-between gap-4">
          <div>
            <Label className="text-sm font-semibold">In-app alerts</Label>
            <div className="text-xs text-muted-foreground mt-1">Always on. View on the Alerts page and dashboard feed.</div>
          </div>
          <Switch checked={true} disabled data-testid="inapp-toggle" />
        </div>

        <Separator />

        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <Label className="text-sm font-semibold flex items-center gap-2"><Mail className="h-4 w-4" /> Email digest <span className="text-xs font-normal text-amber-700">(MOCKED)</span></Label>
            <div className="text-xs text-muted-foreground mt-1">Daily summary of all new EPC changes sent at 02:00 UTC. Currently logged to email-log (no real SMTP integration).</div>
          </div>
          <Switch
            checked={!!s.email_alerts_enabled}
            onCheckedChange={(v) => canEdit && setS({ ...s, email_alerts_enabled: v })}
            disabled={!canEdit}
            data-testid="email-toggle"
          />
        </div>

        {s.email_alerts_enabled && (
          <div className="space-y-2">
            <Label htmlFor="alert_email" className="text-xs uppercase tracking-wider font-semibold">Recipient email</Label>
            <Input
              id="alert_email"
              type="email"
              value={s.alert_email || ""}
              onChange={(e) => setS({ ...s, alert_email: e.target.value })}
              placeholder="alerts@yourhousing.org"
              className="rounded-none border-slate-300"
              disabled={!canEdit}
              data-testid="alert-email-input"
            />
          </div>
        )}

        <Separator />

        <div className="flex items-start justify-between gap-4">
          <div>
            <Label className="text-sm font-semibold">Daily auto-check</Label>
            <div className="text-xs text-muted-foreground mt-1">Automatically scan 200 properties every night at 02:00 UTC (oldest-checked first).</div>
          </div>
          <Switch
            checked={!!s.auto_daily_check}
            onCheckedChange={(v) => canEdit && setS({ ...s, auto_daily_check: v })}
            disabled={!canEdit}
            data-testid="auto-daily-toggle"
          />
        </div>

        {canEdit && (
          <div className="flex justify-between items-center pt-2">
            <Button
              variant="outline"
              size="sm"
              onClick={sendTestDigest}
              disabled={sendingDigest || !s.email_alerts_enabled}
              className="rounded-none border-slate-300"
              data-testid="trigger-digest"
            >
              {sendingDigest ? <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" /> : <Send className="h-3.5 w-3.5 mr-2" />}
              Send test digest now
            </Button>
            <Button onClick={save} disabled={saving} className="rounded-none bg-slate-900 hover:bg-slate-800" data-testid="save-settings">
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              Save changes
            </Button>
          </div>
        )}
      </Card>

      {/* Email log */}
      <Card className="rounded-none border-border p-6" data-testid="email-log">
        <div className="flex items-center gap-2 mb-3">
          <Inbox className="h-4 w-4 text-muted-foreground" />
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Mock inbox</div>
            <h2 className="font-heading text-xl font-bold mt-1">Email log (MOCKED)</h2>
          </div>
        </div>
        <p className="text-xs text-muted-foreground mb-4">
          Every email the system would have sent is logged here. Wire a real provider (Resend / SendGrid) when ready.
        </p>
        {emails.length === 0 ? (
          <div className="text-sm text-muted-foreground py-6 text-center border border-dashed border-border">
            No emails sent yet. Enable email alerts and run "Send test digest now" to populate.
          </div>
        ) : (
          <div className="space-y-2">
            {emails.map((e) => (
              <details key={e.id} className="border border-border p-3 text-sm group" data-testid={`email-${e.id}`}>
                <summary className="cursor-pointer flex items-center justify-between">
                  <div>
                    <div className="font-medium">{e.subject}</div>
                    <div className="text-xs text-muted-foreground font-mono">
                      To: {e.to} · {new Date(e.sent_at).toLocaleString("en-GB")}
                    </div>
                  </div>
                  <span className="text-[10px] uppercase tracking-wider bg-emerald-100 text-emerald-800 px-1.5 py-0.5">{e.status}</span>
                </summary>
                <pre className="mt-3 text-xs whitespace-pre-wrap font-mono bg-slate-50 p-3 border-t border-border">{e.body_text}</pre>
              </details>
            ))}
          </div>
        )}
      </Card>

      {!canEdit && (
        <Alert className="rounded-none border-slate-300 bg-slate-50">
          <AlertDescription className="text-xs text-muted-foreground">
            Your role ({role}) is read-only for settings. Ask an admin or manager to make changes.
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
}
